'use client';

import {
  BoxProps,
  Button,
  Flex,
  Link,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Select,
  Text,
  Textarea,
} from '@mezzoforte/forge';
import { FormEventHandler, useState } from 'react';
import { useLoginModal } from '@/hooks/useLoginModal';
import { useSession } from '@/hooks/useSession';
import { useContactMessage } from '@/hooks/useContactMessages';
import { Contact, ContactTypes, incrementContactStats } from '@/hooks/useEntryStats';
import { useApi } from '@/hooks/useApi';

interface EntryContactProps extends BoxProps {
  companyName: string;
  canCall: boolean;
  canSendMessage: boolean;
  entryId: number;
}

export function EntryContact({ companyName, canCall, canSendMessage, entryId }: EntryContactProps) {
  const [contactModal, setContactModal] = useState<null | ContactTypes>(null);
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [contacts, setContacts] = useState<Contact[]>([]);

  const { LoginModal, openLoginModal } = useLoginModal(false, () => setIsContactModalOpen(true));
  const { currentUser } = useSession();
  const loggedIn = currentUser.data?.isAuthenticated ?? false;

  const { mutate: sendMessage, isPending } = useContactMessage(() => {
    setContactModal(null);
  });

  const { apiClient } = useApi();

  const handleOpenModal = (type: ContactTypes) => {
    if (!loggedIn) {
      openLoginModal();
      return;
    }
    setContactModal(type);
    incrementContactStats(apiClient, entryId, type)
      .then((r) => {
        if (r.success) {
          setContacts(r.contacts);
          setIsContactModalOpen(true);
        } else {
          setContactModal(null);
        }
      })
      .catch(() => {
        setContactModal(null);
      });
  };

  const closeContactModal = () => {
    setIsContactModalOpen(false);
    setContactModal(null);
  };

  const onSubmitMessage: FormEventHandler<HTMLFormElement> = (event) => {
    event.preventDefault();

    const formData = new FormData(event.currentTarget);

    const contactUuid = formData.get('contactUuid')?.toString();
    const message = formData.get('message')?.toString();

    if (contactUuid && message) {
      sendMessage({
        entryId,
        contactUuid,
        message,
      });
    }
  };

  const contactingInfoText = () => (
    <Text my={3}>
      Muistathan, että tarjousten tekeminen järjestelmän ohi ei ole reilua, eikä sallittua toimintaa, vaan on
      säännöissämme ehdottomasti kielletty. Yhteystiedot on tarkoitettu lisätietojen kysymiseen kohteesta. Sääntöjen
      rikkominen voi johtaa käyttäjätunnusten sulkemiseen palvelustamme.
    </Text>
  );

  const renderCallModal = () => (
    <Modal
      headerDivider
      isOpen={isContactModalOpen && contactModal === 'call'}
      onDismiss={closeContactModal}
      data-test="contact-modal-call"
    >
      <ModalHeader>
        Soita ilmoittajalle <span translate="no">{companyName}</span>
      </ModalHeader>
      <ModalBody>
        {contacts.map((contact) => (
          <Text
            mb={3}
            key={`contact-${contact.uuid}`}
          >
            <span translate="no">{contact.name}</span>
            <span>, puhelinnumero: </span>
            <Link
              href={`tel:${contact.phoneNumber}`}
              translate="no"
            >
              {contact.phoneNumber}
            </Link>
          </Text>
        ))}
        {contactingInfoText()}
      </ModalBody>
      <ModalFooter>
        <Button
          onClick={closeContactModal}
          mr={2}
        >
          Sulje
        </Button>
      </ModalFooter>
    </Modal>
  );

  const renderMessageModal = () => {
    if (!currentUser.data?.user) return null;
    return (
      <Modal
        headerDivider
        isOpen={isContactModalOpen && contactModal === 'message'}
        onDismiss={closeContactModal}
        data-test="contact-modal-message"
      >
        <form
          onSubmit={onSubmitMessage}
          style={{ padding: 0 }}
        >
          <ModalHeader>
            Viesti ilmoittajalle <span translate="no">{companyName}</span>
          </ModalHeader>
          <ModalBody>
            {contactingInfoText()}
            <Text my={3}>
              Viestisi lähetetään valitulle ilmoittajalle sähköpostiin. Näkyviin tulee sähköpostiosoitteesi ja nimesi.
              Jos ilmoittaja vastaa viestiisi, lähetetään se antamaasi sähköpostiosoitteeseen{' '}
              {currentUser.data.user.email}.
            </Text>
            <Select
              name="contactUuid"
              label="Vastaanottaja"
              px={0}
              mb={3}
            >
              {contacts.map((contact) => (
                <option
                  key={`contact-${contact.uuid}`}
                  value={contact.uuid}
                  translate="no"
                >
                  {contact.name}
                </option>
              ))}
            </Select>
            <Textarea
              name="message"
              data-testid
              label="Viesti"
            />
          </ModalBody>
          <ModalFooter>
            <Button onClick={closeContactModal}>Sulje</Button>
            <Button
              variant="primary"
              type="submit"
              isDisabled={isPending}
            >
              Lähetä
            </Button>
          </ModalFooter>
        </form>
      </Modal>
    );
  };

  return (
    <Flex>
      <LoginModal />
      {canCall && (
        <Button
          onClick={() => handleOpenModal('call')}
          variant="primary-lists"
          mr={2}
          data-test="contact-button-call"
        >
          Soita
        </Button>
      )}
      {canSendMessage && (
        <Button
          onClick={() => handleOpenModal('message')}
          variant="primary-lists"
          data-test="contact-button-message"
        >
          Lähetä viesti
        </Button>
      )}
      {renderCallModal()}
      {renderMessageModal()}
    </Flex>
  );
}
